import React from "react";
import { Tooltip } from "antd";
import { notesDetail, notesLabel, submittedLine } from "./salaryTotalsData";
import { Money, RiderCell, money, riderCount, toAdminTone } from "./settlementParts";

const EARNINGS = [
  { key: "delivery_charges", label: "Delivery charges", divide: true },
  { key: "tips", label: "Tips" },
  { key: "surge", label: "Surge" },
  { key: "incentive", label: "Incentive", divide: true },
  { key: "special_incentive", label: "Special" },
  { key: "correction", label: "Correction" },
];

const CASH = [
  { key: "cash_collected", label: "Cash collected", divide: true },
  { key: "to_admin", label: "To admin", tone: true },
  { key: "cash_submitted", label: "Cash submitted" },
];

const classOf = (...names) => names.filter(Boolean).join(" ") || undefined;

const MoneyCell = ({ column, value }) =>
  column.tone ? (
    <td className={classOf(column.divide && "stl-divide", toAdminTone(value))}>{money(value)}</td>
  ) : (
    <td className={classOf(column.divide && "stl-divide")}>
      <Money value={value} />
    </td>
  );

const MoneyCells = ({ columns, row }) => columns.map((column) => <MoneyCell key={column.key} column={column} value={row[column.key]} />);

const Note = ({ notes }) => {
  const label = notesLabel(notes);
  if (!label) return "—";
  return notes.length > 1 ? (
    <Tooltip title={<span className="stl-pre">{notesDetail(notes)}</span>}>
      <span className="stl-help">{label}</span>
    </Tooltip>
  ) : (
    label
  );
};

const Header = () => (
  <thead>
    <tr>
      <th className="stl-sticky-start">Rider</th>
      <th>Days</th>
      <th>Orders</th>
      {EARNINGS.map((column) => (
        <th key={column.key} className={classOf(column.divide && "stl-divide")}>
          {column.label}
        </th>
      ))}
      <th className="stl-divide">Salary</th>
      <th className="stl-divide stl-left">Note</th>
      <th className="stl-left">Submitted</th>
      {CASH.map((column) => (
        <th key={column.key} className={classOf(column.divide && "stl-divide")}>
          {column.label}
        </th>
      ))}
      <th className="stl-sticky-end">Cash in hand now</th>
    </tr>
  </thead>
);

const Row = ({ row }) => (
  <tr>
    <td className="stl-sticky-start">
      <RiderCell id={row.id} name={row.name} />
    </td>
    <td>{row.days}</td>
    <td className="stl-strong">{row.orders || 0}</td>
    <MoneyCells columns={EARNINGS} row={row} />
    <td className="stl-divide stl-strong">{money(row.salary)}</td>
    <td className="stl-divide stl-left">
      <Note notes={row.notes} />
    </td>
    <td className="stl-left">{submittedLine(row) || "—"}</td>
    <MoneyCells columns={CASH} row={row} />
    <td className="stl-sticky-end">
      <span className="stl-salary">{money(row.cash_in_hand)}</span>
    </td>
  </tr>
);

const TotalRow = ({ totals }) => (
  <tfoot>
    <tr>
      <td className="stl-sticky-start">
        <div className="stl-rider">
          <span className="stl-rider__name">Total</span>
          <span className="stl-rider__id">{riderCount(totals.riders)}</span>
        </div>
      </td>
      <td>{totals.days}</td>
      <td>{totals.orders}</td>
      <MoneyCells columns={EARNINGS} row={totals} />
      <td className="stl-divide">{money(totals.salary)}</td>
      <td className="stl-divide" />
      <td />
      <MoneyCells columns={CASH} row={totals} />
      <td className="stl-sticky-end">
        <span className="stl-salary">{money(totals.cash_in_hand)}</span>
      </td>
    </tr>
  </tfoot>
);

export default function SubmittedSalariesTable({ riders, totals }) {
  return (
    <div className="stl-scroll stl-scroll--sticky">
      <table className="stl-table">
        <Header />
        <tbody>
          {riders.map((row) => (
            <Row key={row.id} row={row} />
          ))}
        </tbody>
        {totals && <TotalRow totals={totals} />}
      </table>
    </div>
  );
}
